/**
 * Aṣṭakavarga
 *
 * Bhinnāṣṭakavarga — the eight-source benefic-point grid, per graha, from the
 * Parāśarī contribution tables — and Sarvāṣṭakavarga, their sum. Used both for
 * natal strength and, more usefully, for reading transits.
 *
 * The contribution tables are long and unforgiving; they live in data/ and are
 * checked by row and column totals in the tests.
 *
 * ── Scope ───────────────────────────────────────────────────────────────────
 *
 * ◆ BAV, SAV, kakṣyā and the transit lookup (which gochara, Phase 9, stands
 * on), then the two śodhana reductions and the śodhya piṇḍas (BPHS / Parāśara §12.7). The
 * tables were parsed out of the classical Tables 19–26 by tools/parse-ashtakavarga.mjs.
 *
 * ◆ Every function that takes a `kundali` needs only `.grahas[id].rashiIndex`
 * and `.lagna.rashiIndex`, which is also the shape `vargaChart()` returns — so
 * `bhinnashtakavarga(vargaChart(k, 10), 'sun')` is the D-10 grid (BPHS / Parāśara §12.5)
 * with no extra code.
 *
 * ⚠ **Śodhana runs trikoṇa first, then ekādhipatya, and the order matters** —
 * the second reduction reads the output of the first. BPHS / Parāśara §12.7 follows
 * Parāśara in the corner cases (footnote 44: a rāśi with a zero blocks trikoṇa
 * reduction of its trine, even when the other two are unequal).
 *
 * ⚠ **The lagna is one of the eight contributors.** The classical SAV total of
 * 337 includes it. A chart with an unknown birth time has no lagna, so its
 * grid is built from seven contributors and is returned with
 * `lagnaIncluded: false`; totals then fall short of the classical figures and
 * nobody should compare them to those figures.
 *
 * © Manish Jagdish Thatte
 */

import data from '../data/ashtakavarga-tables.json';
import { PHYSICAL_GRAHA_IDS, rashiIndexOf, degreesInRashi } from './constants.js';

const { tables } = data;

/** The eight contributors, in the order of the classical columns. */
export const CONTRIBUTORS = Object.freeze([...PHYSICAL_GRAHA_IDS, 'lagna']);

/** Classical bhinnāṣṭakavarga totals — the check the tables are held to. */
export const BAV_TOTALS = Object.freeze({
  sun: 48, moon: 49, mars: 39, mercury: 54, jupiter: 56, venus: 52, saturn: 39
});

/** §12.4 — 337, the sum of the seven. */
export const SAV_TOTAL = 337;

/**
 * BPHS / Parāśara §12.4: "A rāśi with 30 or more rekhās becomes strong … 25–30 is average …
 * less than 25 becomes weak."
 */
export function savBand(points) {
  if (points >= 30) return 'strong';
  if (points >= 25) return 'average';
  return 'weak';
}

function positionOfContributor(kundali, contributor) {
  if (contributor === 'lagna') return kundali.lagna ? kundali.lagna.rashiIndex : null;
  const g = kundali.grahas?.[contributor];
  return g ? g.rashiIndex : null;
}

/**
 * The bhinnāṣṭakavarga of one graha: rekhās per rāśi (index 0 = Meṣa).
 *
 * `contributions[contributor]` is the 12-array of the points that contributor
 * gave, so the grid can show *why* a rāśi has the count it has.
 */
export function bhinnashtakavarga(kundali, grahaId) {
  const id = String(grahaId).toLowerCase();
  const table = tables[id];
  if (!table || id === 'lagna') {
    throw new RangeError(
      `bhinnashtakavarga: "${grahaId}" has no aṣṭakavarga — only ${PHYSICAL_GRAHA_IDS.join(', ')}`
    );
  }
  const points = new Array(12).fill(0);
  const contributions = {};
  let lagnaIncluded = true;

  for (const c of CONTRIBUTORS) {
    const from = positionOfContributor(kundali, c);
    if (from === null) {
      if (c === 'lagna') lagnaIncluded = false;
      continue;
    }
    const given = new Array(12).fill(0);
    table[c].forEach((bit, h) => {
      if (bit) given[(from + h) % 12] = 1;
    });
    contributions[c] = given;
    given.forEach((v, r) => { points[r] += v; });
  }

  return {
    graha: id,
    points,
    total: points.reduce((a, b) => a + b, 0),
    contributions,
    lagnaIncluded
  };
}

/** Every graha's grid plus their sum. */
export function allBhinnashtakavarga(kundali) {
  const out = {};
  for (const g of PHYSICAL_GRAHA_IDS) out[g] = bhinnashtakavarga(kundali, g);
  return out;
}

/** Sarvāṣṭakavarga — the sum of the seven grids, rāśi by rāśi. */
export function sarvashtakavarga(kundali) {
  const bav = allBhinnashtakavarga(kundali);
  const points = new Array(12).fill(0);
  for (const g of PHYSICAL_GRAHA_IDS) bav[g].points.forEach((v, r) => { points[r] += v; });
  return {
    points,
    total: points.reduce((a, b) => a + b, 0),
    bhinna: bav,
    lagnaIncluded: bav.sun.lagnaIncluded
  };
}

/* ── Kakṣyā — BPHS / Parāśara §25.5.2, Table 60 ──────────────────────────────────────── */

/** Each rāśi is eight kakṣyās of 3°45′, ruled in this order. */
export const KAKSHYA_LORDS = Object.freeze([
  'saturn', 'jupiter', 'mars', 'sun', 'venus', 'mercury', 'moon', 'lagna'
]);

export const KAKSHYA_SPAN_DEG = 30 / 8;

/** The kakṣyā a sidereal longitude lies in: index 0–7 and its lord. */
export function kakshyaOf(siderealLon) {
  const deg = degreesInRashi(siderealLon);
  const index = Math.min(7, Math.floor((deg + 1e-9) / KAKSHYA_SPAN_DEG));
  return { index, lord: KAKSHYA_LORDS[index], startDeg: index * KAKSHYA_SPAN_DEG };
}

/**
 * Kakṣyā bala of a graha at a longitude (natal, or a transit longitude passed in
 * via `atLon`): who rules the kakṣyā, and whether that ruler gave the graha a
 * rekhā in this rāśi. A rekhā from the kakṣyā lord is the favourable case.
 */
export function kakshyaBala(kundali, grahaId, atLon = null) {
  const id = String(grahaId).toLowerCase();
  const lon = atLon ?? kundali.grahas[id].lon;
  const k = kakshyaOf(lon);
  const r = rashiIndexOf(lon);
  const bav = bhinnashtakavarga(kundali, id);
  const given = bav.contributions[k.lord]?.[r] ?? null;
  return {
    graha: id, rashiIndex: r, kakshya: k.index + 1, lord: k.lord,
    // null when the lord is the lagna and the chart has none.
    hasRekha: given === null ? null : given === 1
  };
}

/**
 * What a transit through `rashiIndex` looks like against the natal grids:
 * the graha's own rekhās there (0–8) and the SAV total for the rāśi, with
 * the classical band for the latter.
 */
export function transitPoints(kundali, grahaId, rashiIndex) {
  const r = ((rashiIndex % 12) + 12) % 12;
  const id = String(grahaId).toLowerCase();
  const sav = sarvashtakavarga(kundali);
  const own = tables[id] ? bhinnashtakavarga(kundali, id).points[r] : null;
  return {
    graha: id,
    rashiIndex: r,
    bav: own,
    sav: sav.points[r],
    band: savBand(sav.points[r]),
    lagnaIncluded: sav.lagnaIncluded
  };
}

/* ── Śodhana — BPHS / Parāśara §12.7 ─────────────────────────────────────────────────── */

/**
 * The four sets of mutual trines (§12.7.1): fire, earth, air, water. 0-based.
 */
export const TRIKONA_SETS = Object.freeze([
  Object.freeze([0, 4, 8]),   // Meṣa, Siṁha, Dhanus
  Object.freeze([1, 5, 9]),   // Vṛṣabha, Kanyā, Makara
  Object.freeze([2, 6, 10]),  // Mithuna, Tulā, Kumbha
  Object.freeze([3, 7, 11])   // Karka, Vṛścika, Mīna
]);

/**
 * Rāśis with a common lord (§12.7.2). Karka and Siṁha have one each and take no
 * part. 0-based.
 */
export const EKADHIPATYA_PAIRS = Object.freeze([
  Object.freeze([0, 7]),   // Meṣa – Vṛścika (Maṅgala)
  Object.freeze([1, 6]),   // Vṛṣabha – Tulā (Śukra)
  Object.freeze([2, 5]),   // Mithuna – Kanyā (Budha)
  Object.freeze([8, 11]),  // Dhanus – Mīna (Guru)
  Object.freeze([9, 10])   // Makara – Kumbha (Śani)
]);

/**
 * Table 28 — rāśi multipliers for the rāśi piṇḍa, Meṣa … Mīna.
 *
 * ⚠ **Kanyā is 5, not the 6 that the classical Table 28 prints.** His own worked answers
 * (Exercise 22, all seven grahas of Chart 7) reproduce only with 5, which is also
 * the BPHS figure; with 6 every graha with a rekhā in Kanyā comes out high by
 * that count. It is a misprint in the table, not in the answers. See
 * TABLE_DISCREPANCIES in constants.js.
 */
export const RASHI_MULTIPLIERS = Object.freeze([7, 10, 8, 4, 10, 5, 7, 8, 9, 5, 11, 12]);

/** Table 29 — graha multipliers for the graha piṇḍa. */
export const GRAHA_MULTIPLIERS = Object.freeze({
  sun: 5, moon: 5, mars: 8, mercury: 5, jupiter: 10, venus: 7, saturn: 5
});

/**
 * Trikoṇa śodhana of a 12-array of rekhās (§12.7.1), applied to each trine set
 * separately, with the classical three rules tried in order:
 *
 *   (1) if any of the three is zero, leave the set alone;
 *   (2) if all three are equal, make them all zero;
 *   (3) otherwise subtract the smallest from all three.
 *
 * Returns `{ points, steps }`; `steps[i]` records which rule fired for trine `i`
 * so a reader can follow the reduction rather than trust it.
 *
 * ◆ Rule (2) is (3) with the minimum equal to each value, and rule (1) is (3)
 * with a minimum of zero. the classical text says so himself (footnote 44); they are kept as
 * three explicit branches because the *reasons* are what the page shows.
 */
export function trikonaSodhana(points) {
  requireTwelve(points, 'trikonaSodhana');
  const out = [...points];
  const steps = [];
  for (const set of TRIKONA_SETS) {
    const v = set.map((r) => out[r]);
    if (v.some((x) => x === 0)) {
      steps.push({ set: [...set], rule: 1, before: v, after: v });
      continue;
    }
    if (v[0] === v[1] && v[1] === v[2]) {
      set.forEach((r) => { out[r] = 0; });
      steps.push({ set: [...set], rule: 2, before: v, after: [0, 0, 0] });
      continue;
    }
    const min = Math.min(...v);
    set.forEach((r) => { out[r] -= min; });
    steps.push({ set: [...set], rule: 3, before: v, after: set.map((r) => out[r]) });
  }
  return { points: out, steps };
}

/**
 * Ekādhipatya śodhana (§12.7.2), applied to the output of trikoṇa śodhana.
 *
 * `occupied` is a Set (or array) of the rāśi indices that count as occupied —
 * see `occupiedRashis`. For each pair of co-owned rāśis, in order:
 *
 *   (1) if either has a zero, stop;
 *   (2) if both are occupied, stop;
 *   (3) if exactly one is occupied, the *empty* one is set to zero when its value
 *       is **not higher** than the occupied one's, and to the occupied one's
 *       value when it is higher;
 *   (4) if neither is occupied, equal values both become zero, otherwise the
 *       higher is brought down to the lower.
 *
 * ⚠ **Rule 3 with equal values gives zero.** the classical text writes "lower" for (3a) and
 * "higher" for (3b) and says nothing of a tie, but his Exercise 22 answer for
 * Guru has Dhanus (occupied) and Mīna (empty) both at 2 after trikoṇa śodhana
 * and prints Mīna as 0. So a tie belongs to (3a).
 */
export function ekadhipatyaSodhana(points, occupied) {
  requireTwelve(points, 'ekadhipatyaSodhana');
  const occ = new Set(occupied);
  const out = [...points];
  const steps = [];
  for (const [a, b] of EKADHIPATYA_PAIRS) {
    const va = out[a], vb = out[b];
    const oa = occ.has(a), ob = occ.has(b);
    let rule;
    if (va === 0 || vb === 0) {
      rule = 1;
    } else if (oa && ob) {
      rule = 2;
    } else if (oa !== ob) {
      rule = 3;
      const [emptyR, fullR] = oa ? [b, a] : [a, b];
      // Not higher in the empty rāśi → zero; higher → take the occupied one's.
      out[emptyR] = out[emptyR] <= out[fullR] ? 0 : out[fullR];
    } else {
      rule = 4;
      if (va === vb) { out[a] = 0; out[b] = 0; }
      else if (va > vb) out[a] = vb;
      else out[b] = va;
    }
    steps.push({ pair: [a, b], rule, before: [va, vb], after: [out[a], out[b]] });
  }
  return { points: out, steps };
}

/**
 * The rāśis that count as occupied for ekādhipatya śodhana: those holding one of
 * the seven grahas, **and the lagna's rāśi**.
 *
 * ⚠ The lagna counts. the classical rule says "occupied by a planet", but in his
 * Exercise 22 (Chart 7, lagna in Vṛścika, no graha there) Vṛścika is treated as
 * occupied — Sūrya, Candra and Śani keep their Vṛścika rekhās only if it is —
 * and all seven printed answers reproduce with it and none without. Rāhu and
 * Ketu do not count. Pass `{ includeLagna: false }` to see the difference.
 */
export function occupiedRashis(kundali, { includeLagna = true } = {}) {
  const s = new Set();
  for (const g of PHYSICAL_GRAHA_IDS) {
    const r = kundali.grahas?.[g]?.rashiIndex;
    if (Number.isInteger(r)) s.add(r);
  }
  if (includeLagna && Number.isInteger(kundali.lagna?.rashiIndex)) s.add(kundali.lagna.rashiIndex);
  return s;
}

/**
 * Sodhita aṣṭakavarga and śodhya piṇḍas of one graha (§12.7.3).
 *
 * Returns `{ graha, bav, trikona, ekadhipatya, sodhita, rashiPinda,
 * grahaPinda, sodhyaPinda }`. `trikona` and `ekadhipatya` are `{points, steps}`
 * so each stage can be shown; `sodhita` is the final 12-array.
 *
 * Rāśi piṇḍa = Σ (śodhita rekhās × Table 28 multiplier) over the twelve rāśis.
 * Graha piṇḍa = Σ (śodhita rekhās in the graha's rāśi × Table 29 multiplier)
 * over the seven grahas — *every* graha's position read in *this* graha's grid.
 */
export function sodhana(kundali, grahaId) {
  const bav = bhinnashtakavarga(kundali, grahaId);
  const trikona = trikonaSodhana(bav.points);
  const ekadhipatya = ekadhipatyaSodhana(trikona.points, occupiedRashis(kundali));
  const sodhita = ekadhipatya.points;

  const rashiPinda = sodhita.reduce((sum, v, r) => sum + v * RASHI_MULTIPLIERS[r], 0);
  let grahaPinda = 0;
  for (const g of PHYSICAL_GRAHA_IDS) {
    const r = kundali.grahas?.[g]?.rashiIndex;
    if (Number.isInteger(r)) grahaPinda += sodhita[r] * GRAHA_MULTIPLIERS[g];
  }

  return {
    graha: bav.graha,
    bav: bav.points,
    trikona,
    ekadhipatya,
    sodhita,
    rashiPinda,
    grahaPinda,
    sodhyaPinda: rashiPinda + grahaPinda,
    lagnaIncluded: bav.lagnaIncluded
  };
}

/** Śodhana for all seven grahas. */
export function allSodhana(kundali) {
  const out = {};
  for (const g of PHYSICAL_GRAHA_IDS) out[g] = sodhana(kundali, g);
  return out;
}

/**
 * Everything the strength page shows, in one call: the seven grids, SAV, the
 * reduced grids and piṇḍas.
 */
export function ashtakavarga(kundali) {
  const sav = sarvashtakavarga(kundali);
  return {
    bhinna: sav.bhinna,
    sarva: { points: sav.points, total: sav.total },
    sodhana: allSodhana(kundali),
    lagnaIncluded: sav.lagnaIncluded
  };
}

function requireTwelve(points, who) {
  if (!Array.isArray(points) || points.length !== 12 || points.some((v) => !Number.isFinite(v))) {
    throw new TypeError(`${who}: expected an array of twelve numbers`);
  }
}
